/**
 * 案件の憲章の読取（`getPjCharter`）の入出力契約と、案件番号から Backlog の課題キーを組む規則。
 *
 * **憲章の正は仕様グラフではなく Firestore `spec_charters` である**（起票のフォームが書く）。
 * 起票の仕組みより前の案件は、案件仕様書の「プロジェクト憲章」タブにしか無い。読む順番
 * （Firestore → 案件仕様書）は `@cortex/domain-spec-flow` の `readCharter` が持ち、API も
 * project-management の MCP `get_charter` も同じ関数を通す。鍵は Backlog の課題キー（`AIRCLOSET-143394`）で、
 * 仕様グラフの案件番号は課題キーの数値部分がそのまま入る（決定。2026-09-21 — 共通部の
 * `specPjNumberValue`）。だから課題キーは番号から一意に組める。
 *
 * **前置きの綴りは共通部（`@cortex/domain-spec-graph` の `PJ_KEY_PREFIX` / `pjKeyOf`）に 1 つだけ置き、
 * ここは再輸出する。** API は憲章を引く鍵に、共通部は案件の一覧の件名を引く鍵に、画面は案件番号の
 * 表記に同じ前置きを使う。片方だけ直すと、画面の綴りと読む鍵が黙ってずれる。
 *
 * **欄の並びは憲章の正（`@cortex/domain-spec-flow` の `CHARTER_FIELDS`）の写しである。** 契約は
 * 画面にも読まれるので、Firestore のクライアントを持つ正の package を import できない。写しがずれると
 * API のテストが落ちる（`infrastructure/charter/pj-charter.test.ts`）。読み元（`PjCharterSourceSchema`）も同じ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の憲章の読取の入出力契約（値のある欄を正の並びで返す・保存されていなければ null）と、案件番号から Backlog の課題キーを組む規則を定義する。
 * @graph-connects none
 */
import { z } from 'zod';
import { NonEmptyStringSchema } from '../spec-graph-common.js';
import { PjNumberSchema } from './pj-operations.js';
// 課題キーの前置きと組み方の正は共通部に置く（案件の一覧の件名を引く鍵と同じ規則を使う）。
export { PJ_KEY_PREFIX, pjKeyOf } from '@cortex/domain-spec-graph';
/**
 * 憲章の欄。**並びが画面に出す順**である（目的から備考まで）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 憲章の 10 欄を、画面に出す順に並べた閉集合
 * @graph-connects none
 */
export const PJ_CHARTER_FIELDS = [
    'purpose',
    'background',
    'goal',
    'deliverables',
    'preconditions',
    'constraints',
    'schedule',
    'cost',
    'structure',
    'notes',
];
/**
 * 憲章の欄の名前。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 憲章の欄の名前の出力スキーマ
 * @graph-connects none
 */
export const PjCharterFieldSchema = z.enum(PJ_CHARTER_FIELDS);
/**
 * 値のある欄 1 つ。値は前後の空白を落としたもので、空にはならない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 憲章の値のある欄 1 つ（欄の名前と本文）の出力スキーマ
 * @graph-connects none
 */
export const PjCharterEntrySchema = z.object({
    field: PjCharterFieldSchema,
    value: NonEmptyStringSchema,
});
/**
 * `getPjCharter` の入力。案件番号だけを取る。
 *
 * 言語は取らない。本文は起票した人が書いた原文で訳す対象ではなく、欄の名前は画面が訳す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の憲章の入力スキーマ（案件番号）
 * @graph-connects none
 */
export const GetPjCharterInputSchema = z.object({
    pjNumber: PjNumberSchema,
});
/**
 * 憲章をどこから読んだか。`firestore` は起票の仕組みが保存した憲章、`specSheet` は案件仕様書の
 * 「プロジェクト憲章」タブ（起票の仕組みより前の案件）。値の正は `@cortex/domain-spec-flow` の
 * `CHARTER_SOURCES`（写しのずれは API のテストが止める）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 憲章の読み元（Firestore か案件仕様書か）の閉集合
 * @graph-connects none
 */
export const PjCharterSourceSchema = z.enum(['firestore', 'specSheet']);
/**
 * `getPjCharter` の出力。
 *
 * 読む順番は Firestore → 案件仕様書の「プロジェクト憲章」タブ（`@cortex/domain-spec-flow` の
 * `readCharter`。MCP の `get_charter` と同じ関数）。`source` がどちらから読んだかを表す。
 *
 * `charter` が null なのは、憲章がどちらにも無いときと、案件仕様書を読めなかったときである。
 * 後者は `specSheetUnreadable` が true になる（共有されていない 403 を含む）— 読めなかった憲章を
 * 「無い」と畳まない。憲章があって全欄が空なら、`fields` が空の配列で返る — 「無い」と「空」も畳まない。
 * `fields` は値のある欄だけを `PJ_CHARTER_FIELDS` の順に並べる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の憲章の出力スキーマ（無ければ null、あれば読み元と値のある欄を正の並びで、案件仕様書を読めなければその旨）
 * @graph-connects none
 */
export const GetPjCharterOutputSchema = z.object({
    charter: z
        .object({
        source: PjCharterSourceSchema,
        fields: z.array(PjCharterEntrySchema),
    })
        .nullable(),
    specSheetUnreadable: z.boolean(),
});
//# sourceMappingURL=pj-charter.js.map